import { useState } from 'react'
import { Button } from '@/shared/ui/button'
import { Dialog } from '@/shared/ui/dialog'
import { Field } from '@/shared/ui/field'
import { Icon } from '@/shared/ui/icon'
import { toast } from '@/shared/ui/toast'
import { useSetEntityTags } from '../api/mutations'
import type { TagEntityKind } from '../model/types'
import { EntityTagsInput } from './entity-tags-input'
import { TagChips } from './tag-chips'

interface EntityTagsEditorProps {
  entityKind: TagEntityKind
  entityId: string
  tags: string[]
  /** Клик по чипу (фильтр/поиск) — маршрут решает потребитель. */
  onTagClick: (tag: string) => void
}

// Ряд тегов сущности (контур сотрудника): чипы + карандаш-правка. Запись —
// отдельный PUT /tags (replace-семантика), не PATCH сущности. Роутинг клика по
// тегу остаётся у потребителя (onTagClick), слой entities роутер не знает.
export function EntityTagsEditor({
  entityKind,
  entityId,
  tags,
  onTagClick,
}: EntityTagsEditorProps) {
  const [editOpen, setEditOpen] = useState(false)

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <TagChips tags={tags} onTagClick={onTagClick} />
      <button
        type="button"
        aria-label="Изменить теги"
        onClick={() => setEditOpen(true)}
        className="inline-flex items-center gap-1 rounded-control px-1.5 py-1 text-xs text-text-tertiary transition-colors hover:text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-accent"
      >
        <Icon name="pencil" size={13} />
        {tags.length === 0 ? 'Теги' : null}
      </button>
      {editOpen && (
        <EditTagsDialog
          entityKind={entityKind}
          entityId={entityId}
          tags={tags}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}

function EditTagsDialog({
  entityKind,
  entityId,
  tags,
  onClose,
}: {
  entityKind: TagEntityKind
  entityId: string
  tags: string[]
  onClose: () => void
}) {
  const setTags = useSetEntityTags(entityKind, entityId)
  const [draft, setDraft] = useState(tags)

  function save() {
    setTags.mutate(draft, {
      onSuccess: onClose,
      onError: () => toast.error('Не удалось сохранить теги'),
    })
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Теги"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={setTags.isPending}>
            Отмена
          </Button>
          <Button onClick={save} disabled={setTags.isPending}>
            {setTags.isPending ? 'Сохраняем…' : 'Сохранить'}
          </Button>
        </>
      }
    >
      <Field label="Теги">
        {(props) => <EntityTagsInput {...props} value={draft} onChange={setDraft} />}
      </Field>
    </Dialog>
  )
}
